import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { RegistrationsSummary } from "../../../sdk/@types/Simulado";
import type { Fase, RegistrationEntry } from "./inscritosVocabulary";

interface InscritosResumoProps {
  summary: RegistrationsSummary;
  fase: Fase;
  mocktestStart: string;
  registrations: RegistrationEntry[];
  /**
   * O mesmo instante que `InscritosPanel` já usou para derivar `fase` —
   * nunca um novo `new Date()` aqui, senão "Começa em" e a fase podem
   * discordar sobre quando é "agora" (design.md de
   * `simulado-cancelamento-efetivo`, cenário "O início do simulado tem uma
   * só origem").
   */
  now: Date;
}

interface StatTile {
  key: string;
  label: string;
  value: string;
  detail?: string;
  isPercent?: boolean;
  dot: string;
}

function formatPct(count: number, base: number): string {
  return base > 0 ? `${Math.round((count / base) * 100)}%` : "0%";
}

function formatMedia(value: number): string {
  return value.toFixed(1).replace(".", ",");
}

// "Começa em" leads with the countdown (spec.md: "quanto falta para
// começar"), not the absolute date — the frame puts the date+time in the
// tile's detail slot instead (same slot every other tile uses for its %).
function formatQuantoFalta(mocktestStart: string, now: Date): string {
  const days = Math.max(0, differenceInCalendarDays(parseISO(mocktestStart), now));
  if (days === 0) return "Hoje";
  if (days === 1) return "1 dia";
  return `${days} dias`;
}

// Frame dots are raw per-tile colors with no visible semantic rule (checked
// directly against every tile via mcp__pencil). Only two have an established
// product-token equivalent — the same `ok`/`warn` the status badges already
// use for these exact outcomes (inscritosVocabulary's STATUS_TONE_CLASSNAME)
// — so only those two carry color; everything else, including "Cancelados"
// (design.md Decision 8; AC5), stays neutral rather than inventing an
// unmapped token.
function dotClassName(key: string): string {
  if (key === "concluiram") return "bg-ok";
  if (key === "incompletos") return "bg-warn";
  return "bg-ink-muted";
}

// Média é a única tile que "durante" e "depois" compartilham — mesmo valor
// e o mesmo `null` → "—" (nunca "0"/"0%", AC6). Só "depois" declara o
// percentual: a spec.md descreve "Em andamento" sem nenhuma menção a
// percentuais, e só "Depois do fim" diz "cada um com o seu percentual" —
// a base dos percentuais ainda não é nomeada na tela (fica para
// `resumo-base-declarada-e-empilhamento`), então "durante" não mostra um
// percentual não-nomeado.
function buildMediaTile(
  mediaAcertos: number | null,
  totalQuestions: number | undefined,
  withPercent: boolean,
): Omit<StatTile, "dot"> {
  if (mediaAcertos === null) {
    return { key: "media", label: "Média de acertos", value: "—" };
  }
  return {
    key: "media",
    label: "Média de acertos",
    value: `${formatMedia(mediaAcertos)} / ${totalQuestions ?? 0}`,
    detail: withPercent ? formatPct(mediaAcertos, totalQuestions ?? 0) : undefined,
    isPercent: withPercent,
  };
}

function buildTiles(
  summary: RegistrationsSummary,
  fase: Fase,
  now: Date,
  mocktestStart: string,
  registrations: RegistrationEntry[],
): StatTile[] {
  const totalLabel = summary.total.toLocaleString("pt-BR");
  const totalQuestions = registrations[0]?.totalQuestions;

  if (fase === "antes") {
    const tiles: Array<Omit<StatTile, "dot">> = [
      { key: "total", label: "Inscritos", value: totalLabel },
      {
        key: "novos",
        label: "Novos (últimos 7 dias)",
        value: summary.novosUltimos7Dias.toLocaleString("pt-BR"),
      },
      {
        key: "cancelados",
        label: "Cancelados",
        value: summary.cancelados.toLocaleString("pt-BR"),
      },
      {
        key: "comeca-em",
        label: "Começa em",
        value: formatQuantoFalta(mocktestStart, now),
        detail: format(parseISO(mocktestStart), "dd/MM '·' HH:mm", { locale: ptBR }),
      },
    ];
    return tiles.map((tile) => ({ ...tile, dot: dotClassName(tile.key) }));
  }

  if (fase === "durante") {
    // Mesmos campos que "depois" lê — `summary.incompletos` e
    // `summary.naoIniciaram` (design.md, Decisão 2; AC3) — só o rótulo é
    // outro, porque o simulado ainda está correndo e "Incompletos"/"Não
    // iniciaram" leriam como desfecho de uma prova já encerrada (AC2).
    // Chaves próprias — "respondendo"/"sem-resposta", não
    // "incompletos"/"nao-iniciaram" — para que `dotClassName` não aplique
    // aqui o tom `warn` que aquela chave carrega na fase "depois": lá
    // "incompleto" é um desfecho a sinalizar, aqui é só quem está
    // respondendo agora, nada a destacar.
    const tiles: Array<Omit<StatTile, "dot">> = [
      { key: "total", label: "Inscritos", value: totalLabel },
      {
        key: "respondendo",
        label: "Responderam alguma coisa",
        value: summary.incompletos.toLocaleString("pt-BR"),
      },
      {
        key: "sem-resposta",
        label: "Ainda não responderam",
        value: summary.naoIniciaram.toLocaleString("pt-BR"),
      },
      {
        key: "cancelados",
        label: "Cancelados",
        value: summary.cancelados.toLocaleString("pt-BR"),
      },
      buildMediaTile(summary.mediaAcertos, totalQuestions, false),
    ];
    return tiles.map((tile) => ({ ...tile, dot: dotClassName(tile.key) }));
  }

  const { total, concluidos, incompletos, naoIniciaram, cancelados } = summary;

  const tiles: Array<Omit<StatTile, "dot">> = [
    { key: "total", label: "Inscritos", value: totalLabel },
    {
      key: "concluiram",
      label: "Concluíram",
      value: concluidos.toLocaleString("pt-BR"),
      detail: formatPct(concluidos, total),
      isPercent: true,
    },
    {
      key: "incompletos",
      label: "Incompletos",
      value: incompletos.toLocaleString("pt-BR"),
      detail: formatPct(incompletos, total),
      isPercent: true,
    },
    {
      key: "nao-iniciaram",
      label: "Não iniciaram",
      value: naoIniciaram.toLocaleString("pt-BR"),
      detail: formatPct(naoIniciaram, total),
      isPercent: true,
    },
    {
      // Kept visible after the mocktest starts (design.md Decisão 4 of
      // simulado-cancelamento-efetivo) — the finished-phase counters must sum
      // to the displayed total, and summary.total already includes cancellations.
      key: "cancelados",
      label: "Cancelados",
      value: cancelados.toLocaleString("pt-BR"),
      detail: formatPct(cancelados, total),
      isPercent: true,
    },
    buildMediaTile(summary.mediaAcertos, totalQuestions, true),
  ];
  return tiles.map((tile) => ({ ...tile, dot: dotClassName(tile.key) }));
}

export function InscritosResumo({ summary, fase, mocktestStart, registrations, now }: InscritosResumoProps) {
  const tiles = buildTiles(summary, fase, now, mocktestStart, registrations);
  // Generic over phase on purpose — declares the base whenever ANY tile
  // currently shows a percentage, rather than hardcoding `fase === "depois"`.
  const showsPercent = tiles.some((tile) => tile.isPercent);
  const totalLabel = summary.total.toLocaleString("pt-BR");

  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold text-ink">Resumo</h3>
      {/* Stacked below md, one row at md and up — md is this product's
          established "stack to one column" boundary (see Home.view.tsx's own
          md:grid-cols-* tile grids). Dividers flip side with the axis so a
          stacked column never shows a vertical rule between rows. */}
      <div className="flex flex-col rounded-card border border-edge bg-surface md:flex-row">
        {tiles.map((tile, i) => (
          <div
            key={tile.key}
            className={`flex flex-col gap-1 px-[18px] py-[14px] md:flex-1 ${
              i < tiles.length - 1 ? "border-b border-edge md:border-b-0 md:border-r" : ""
            }`}
          >
            <div className="flex items-center gap-1.5">
              <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${tile.dot}`} />
              <span className="text-xs font-medium text-ink-muted">{tile.label}</span>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-2xl font-bold text-ink">{tile.value}</span>
              {tile.detail && (
                <span className="pb-0.5 text-xs font-medium text-ink-muted">{tile.detail}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {showsPercent && (
        <p className="mt-2 text-xs text-ink-muted">
          Percentuais sobre o total de {totalLabel} inscritos.
        </p>
      )}
    </section>
  );
}

export default InscritosResumo;
